import { getDialogConfig } from 'src/app/util/dialog-util';
import { getDatasetLockedDialogData, isDatasetLockedError } from 'src/app/util/dataset-lock-util';
import { showApiErrorDialog } from 'src/app/util/api-error-util';
import { TagDto } from './../../../../data-transfer/entities/tag-dto';
import { ParentNavigationService } from './../../../../navigation/services/navigation-services/parent-navigation-service';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { concatMap, filter, finalize } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { MatSort } from '@angular/material/sort';
import { OrganizationDto } from './../../../../data-transfer/entities/organization-dto';
import { Component, DestroyRef, inject, Inject, OnDestroy, OnChanges, SimpleChanges, signal, ViewChild, LOCALE_ID, Input, EventEmitter, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalysisMenuCaller, PackagingValidationMapping } from '../../analyses-menu/analyses-menu-parent/analyses-menu-parent.component';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { ExportDialogComponent } from 'src/app/components/directory-tree/dialogs/export-dialog/export-dialog.component';
import { DirectoryApiService } from 'src/app/data-transfer/services/directory-api-service';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { PackagingComponentExportProfileDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-export-profile-dto';
import { CompositeMaterialExportProfileDto } from 'src/app/data-transfer/entities/material-entities/composite-material-export-profile-dto';
import { PackagingUnitExportProfileDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-export-profile-dto';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { PackagingSystemExportProfileDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-export-profile-dto';
import { DialogActions } from 'src/app/model/dictionary';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { PackagingSystemDto } from 'src/app/data-transfer/entities/packaging-system-entities/packaging-system-dto';
import { PackagingUnitDto } from 'src/app/data-transfer/entities/packaging-unit-entities/packaging-unit-dto';
import { PackagingComponentDto } from 'src/app/data-transfer/entities/component-entities/packaging-component-dto';
import { MultiMaterialCompositeDto } from 'src/app/data-transfer/entities/material-entities/multi-material-composite-dto';
import {
  CreateDataExchangeRequestDialogComponent,
  CreateDataExchangeRequestDialogData,
} from 'src/app/components/directory-tree/dialogs/create-data-exchange-request-dialog/create-data-exchange-request-dialog.component';
import { CreateDataExchangeRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/create-data-exchange-request-dto';
import { DataExchangeRequestStatusDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-request-status-dto';
import { ColumnDefinitionDto, UserProfileDto } from 'src/app/data-transfer/entities/user-profile-dto';
import { ColumnOrganizerDialogComponent, ColumnOrganizerDialogData } from 'src/app/components/dialogs/column-organizer-dialog/column-organizer-dialog.component';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';

@Component({
  selector: 'app-overview-table',
  templateUrl: './overview-table.component.html',
  styleUrls: ['./overview-table.component.scss'],
})
export abstract class OverviewTableComponent implements OnDestroy, OnChanges {
  @ViewChild(MatSort) sort!: MatSort;

  @Input() allTags: TagDto[] = [];
  @Input() sortByColumnName?: string;
  @Input() userProfile?: UserProfileDto;
  @Input() showColumnOrganizer = false;

  @Output() packagingPartsSelected = new EventEmitter();
  @Output() userProfileChanged = new EventEmitter<UserProfileDto>();

  allItemsSelected = false;
  organizations: OrganizationDto[] = [];
  filteredColumns: string[] = [];
  callerPageId = AnalysisMenuCaller.Homepage;
  validationDataList: PackagingValidationMapping[] = [];

  displayTrackedColumn = false;
  isCreatingDataExchangeRequest = signal(false);
  public abstract displayedColumns: string[];

  protected abstract columnCatalog: ColumnDefinitionDto[];
  protected abstract columnSettingsTableType: string;
  protected abstract entityLabelKey: string;

  protected manageableColumnKeys: string[] = [];
  visibleManageableColumns: string[] = [];

  protected moveBackendSubscription?: Subscription;
  protected copyBackendSubscription?: Subscription;
  protected organizationsSubscription?: Subscription;
  protected itemsInDirSubscription?: Subscription;
  protected deleteBackendSubscription?: Subscription;
  protected restoreSubscription?: Subscription;
  protected comparisonSubscription?: Subscription;
  protected dialogSubscription?: Subscription;
  protected permissionsSubscription?: Subscription;
  protected dataSourceSubscription?: Subscription;
  protected recSubscription?: Subscription;
  protected jsonSubscription?: Subscription;
  protected excelSubscription?: Subscription;
  private exportBackendSubscription?: Subscription;
  private columnOrganizerDialogSubscription?: Subscription;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    protected dialog: MatDialog,
    protected directoryApiService: DirectoryApiService,
    protected importExportApiService: ImportExportApiService,
    protected navigationService: ParentNavigationService,
    protected translateService: TranslocoService,
    protected userApiService: UserApiService,
    protected coreDataUpdateService: CoreDataUpdateService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) protected locale: string
  ) {}

  public abstract movePackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]): void;

  public abstract copyPackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]): void;

  public abstract deletePackagingPart(): void;

  public abstract restorePackagingPart(): void;

  public abstract exportPackagingPart(): void;

  public abstract comparePackagingPart(): void;

  public abstract editPackagingPartQuantity(): void;

  public abstract createDataExchangeRequest(): void;

  public abstract dataSource: PaginationDataSource<any> | MatTableDataSource<any>;
  public abstract getPaginationFieldName(column: string): string;

  getActiveSortColumn(): string {
    const dataSource = this.dataSource;
    if (!(dataSource instanceof PaginationDataSource)) {
      return this.sortByColumnName ?? '';
    }
    const sortField = dataSource.sortByField;
    if (!sortField) {
      return this.sortByColumnName ?? '';
    }
    return this.displayedColumns.find(col => this.getPaginationFieldName(col) === sortField) ?? this.sortByColumnName ?? '';
  }

  getActiveSortDirection(): 'asc' | 'desc' {
    return this.dataSource instanceof PaginationDataSource && this.dataSource.sortAscending ? 'asc' : 'desc';
  }

  readonly DataExchangeRequestStatusDto = DataExchangeRequestStatusDto;

  getSubRequestStatusLabel(status: DataExchangeRequestStatusDto | undefined): string {
    if (status == null) {
      return '';
    }
    return `dataManagement.dataExchangeRequests.status.${DataExchangeRequestStatusDto[status]}`;
  }

  public abstract resetSelection(): void;

  public abstract getTagsSettingFunction(isEditing: boolean): (id: number, tagIds: number[]) => Observable<any>;

  public abstract getPackagingPartFunction(): (id: number) => Observable<PackagingSystemDto | PackagingUnitDto | PackagingComponentDto | MultiMaterialCompositeDto>;

  protected setDataSourceFilter(dataSource: MatTableDataSource<any>) {
    dataSource.filterPredicate = (data, filter: any) => {
      let result = true;

      this.filteredColumns = Object.keys(filter.criterion);

      if (filter.criterion) {
        for (const key of this.filteredColumns) {
          const searchCondition = filter.criterion[key];

          if (searchCondition) {
            if (filter.allCriteria[searchCondition](this.getFilterValue(data, key), filter.value[key]) === false) {
              result = false;
              break;
            }
          }
        }
      }
      return result;
    };
  }

  protected getFilterValue(data: any, key: string): any {
    if (key === 'associatedTagIdentifiers') {
      const ids = data[key] as number[] | undefined;
      return this.getTagsFromIds(ids ?? []).map(tag => tag.name);
    }

    return data[key];
  }

  protected updateDisplayTrackedColumn(dataSource: MatTableDataSource<any>) {
    if (dataSource.paginator) {
      const startIndex = dataSource.paginator.pageIndex * dataSource.paginator.pageSize;
      this.displayTrackedColumn = dataSource.filteredData.slice(startIndex, startIndex + dataSource.paginator.pageSize).filter(x => x.hasExternalTracking).length > 0;
    } else {
      this.displayTrackedColumn = dataSource.filteredData.filter(x => x.hasExternalTracking).length > 0;
    }
  }

  formatDateToString(timestamp: string) {
    if (timestamp) {
      return new Date(timestamp).toLocaleDateString(this.locale);
    } else {
      return '';
    }
  }

  getDate(dateStr: string | undefined): Date | null {
    if (dateStr == null) {
      return null;
    }
    return new Date(dateStr);
  }

  getOrganizationName(organizationId: number): string {
    if (this.organizations.length > 0 && organizationId != null) {
      return this.organizations.find(x => x.id === organizationId)?.name ?? '';
    }
    return '';
  }

  setValidationData(e: PackagingValidationMapping) {
    this.validationDataList.push(e);
  }

  isFullyValidatedAllValid(packagingUnitId: number) {
    const allVersions = this.validationDataList.filter(x => x.id === packagingUnitId);
    const allInvalidVersions = allVersions.filter(x => x.isFullyValidatedAllValid === false || x.isNotFullyValidated === true);
    return allInvalidVersions.length === 0 && allVersions.length > 0;
  }

  isFullyValidatedNotAllValid(packagingUnitId: number) {
    const allVersions = this.validationDataList.filter(x => x.id === packagingUnitId);
    const allInvalidVersions = allVersions.filter(x => x.isFullyValidatedNotAllValid === true);
    const notAllValidated = allVersions.filter(x => x.isNotFullyValidated === true);
    return allInvalidVersions.length > 0 && notAllValidated.length === 0;
  }

  isNotFullyValidated(packagingUnitId: number) {
    const allVersions = this.validationDataList.filter(x => x.id === packagingUnitId);
    const notAllValidated = allVersions.filter(x => x.isNotFullyValidated === true);
    return notAllValidated.length > 0;
  }

  isTouched(packagingUnitId: number) {
    const allVersions = this.validationDataList.filter(x => x.id === packagingUnitId);
    return allVersions.find(x => x.isUntouched === false);
  }

  getExportSuccessDialogData(translateService: TranslocoService): SimpleDialogData {
    return {
      title: translateService.translate('common.text.success'),
      messages: [translateService.translate('dataManagement.directory.dialog.export.exportSuccess')],
      icon: 'info',
    };
  }

  getExportErrorDialogData(translateService: TranslocoService): SimpleDialogData {
    return {
      title: translateService.translate('common.text.error'),
      messages: [translateService.translate('dataManagement.directory.dialog.export.exportError')],
      icon: 'error',
    };
  }

  getDeleteNotAllowedData(translateService: TranslocoService): SimpleDialogData {
    return {
      title: translateService.translate('common.text.error'),
      messages: [translateService.translate('warnings.deleteNotPermitted')],
      icon: 'error',
    };
  }

  getTagsFromIds(ids: number[]): TagDto[] {
    return this.allTags.filter(tag => ids.includes(tag.id ?? -1));
  }

  getAllTags(): string[] {
    return this.allTags.map(tag => tag.name);
  }

  getPossibleTags(): { name: string; value: any }[] {
    return this.allTags.map(tag => ({ name: tag.name, value: tag.id }));
  }

  protected async exportItems(
    dialogConfig: MatDialogConfig,
    exportProfilesFunction: () => Observable<
      PackagingSystemExportProfileDto[] | PackagingUnitExportProfileDto[] | PackagingComponentExportProfileDto[] | CompositeMaterialExportProfileDto[]
    >,
    exportFunction: (id: number, principalId: number, profileId?: number, subprofileId?: number, stayOnPageIfError?: boolean) => Observable<any>,
    isPackagingSystemExport = false
  ) {
    const securityPrincipals = await this.directoryApiService.getExternalSecurityPrincipals().toPromise();
    if (!securityPrincipals) {
      return;
    }
    const exportProfiles = await exportProfilesFunction().toPromise();
    dialogConfig.data.allPrincipals = securityPrincipals;
    dialogConfig.data.allProfiles = exportProfiles;
    if (isPackagingSystemExport) {
      const subExportProfiles = await this.importExportApiService.getPackagingUnitExportProfiles().toPromise();
      dialogConfig.data.allSubProfiles = subExportProfiles;
    }
    this.spinner.hide();
    const dialogRef = this.dialog.open(ExportDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) {
        return;
      }
      const exportResult: Observable<any>[] = [];
      const profileId = result.profileId;
      const subprofileId = result.subprofileId ?? null;
      for (const principalId of result.selectedPrincipalIds) {
        for (const packagingId of result.selectedIds) {
          exportResult.push(exportFunction(packagingId, principalId, profileId, subprofileId, true));
        }
      }
      this.exportBackendSubscription = forkJoin(exportResult).subscribe({
        next: _ => {
          const dialogData = this.getExportSuccessDialogData(this.translateService);
          this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(dialogData, '500px'));
        },
        error: error => {
          const dialogData = isDatasetLockedError(error)
            ? getDatasetLockedDialogData(error, this.translateService)
            : this.getExportErrorDialogData(this.translateService);
          this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(dialogData, '500px'));
        },
      });
    });
  }

  protected openCreateDataExchangeRequestDialog(
    subRequestData: CreateDataExchangeRequestDialogData,
    createFn: (dto: CreateDataExchangeRequestDto) => Observable<any>
  ): void {
    if (this.isCreatingDataExchangeRequest()) {
      return;
    }
    this.isCreatingDataExchangeRequest.set(true);

    this.directoryApiService.getExternalSecurityPrincipals().pipe(
      finalize(() => this.isCreatingDataExchangeRequest.set(false)),
      concatMap(securityPrincipals => {
        const dialogData: CreateDataExchangeRequestDialogData = { ...subRequestData, allPrincipals: securityPrincipals };
        return this.dialog.open(CreateDataExchangeRequestDialogComponent, getDialogConfig(dialogData, '50%')).afterClosed();
      }),
      filter(result => !!result && result.event !== DialogActions.REJECT),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(result => {
      this.spinner.show();
      createFn(result.dto).subscribe({
        next: _ => {
          this.spinner.hide();
          const data: SimpleDialogData = {
            title: this.translateService.translate('common.text.success'),
            messages: [this.translateService.translate('dataManagement.directory.dialog.dataExchangeRequest.createSuccess')],
            icon: 'info',
          };
          this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(data, 'm'));
          this.onDataExchangeRequestCreated();
        },
        error: error => {
          this.spinner.hide();
          showApiErrorDialog(
            this.dialog, error, this.translateService,
            this.translateService.translate('dataManagement.directory.dialog.dataExchangeRequest.createError'));
        },
      });
    });
  }
  protected onDataExchangeRequestCreated(): void {  }

  protected flattenDirectories(rootDirectory: DirectoryDto): { name: string; value: number }[] {
    const directories: { name: string; value: number }[] = [{ name: rootDirectory.name, value: rootDirectory.id }];

    for (const childDirectory of rootDirectory.childDirectories) {
      directories.push(...this.flattenDirectories(childDirectory));
    }

    return directories;
  }

  protected applyColumnSettings(visibleManageableColumns: string[], manageableColumnKeys: string[]): void {
    const manageableSet = new Set(manageableColumnKeys);
    const originalIndex = this.displayedColumns.findIndex(c => manageableSet.has(c));
    const fixedColumns = this.displayedColumns.filter(c => !manageableSet.has(c) && c !== 'select');
    const index = originalIndex === -1 ? fixedColumns.length : this.displayedColumns.slice(0, originalIndex).filter(c => !manageableSet.has(c) && c !== 'select').length;
    const columns = [...fixedColumns.slice(0, index), ...visibleManageableColumns, ...fixedColumns.slice(index)];
    if (this.displayedColumns.includes('select')) {
      columns.push('select');
    }
    this.displayedColumns = columns;
  }

  protected openColumnOrganizer(columnCatalog: ColumnDefinitionDto[], visibleColumns: string[], entityLabelKey: string, onSave: (newVisibleColumns: string[]) => void): void {
    const data: ColumnOrganizerDialogData = { columnCatalog, visibleColumns: [...visibleColumns], entityLabelKey };
    const dialogRef = this.dialog.open(ColumnOrganizerDialogComponent, getDialogConfig(data, '600px'));
    this.columnOrganizerDialogSubscription = dialogRef.afterClosed().subscribe((result: string[] | undefined) => {
      if (!result) {
        return;
      }
      onSave(result);
    });
  }

  getColumnCatalog(): ColumnDefinitionDto[] {
    return this.columnCatalog;
  }

  openColumnSettingsDialog(): void {
    this.openColumnOrganizer(this.getColumnCatalog(), this.visibleManageableColumns, this.entityLabelKey, newVisibleColumns => {
      this.visibleManageableColumns = newVisibleColumns;
      this.applyColumnSettings(newVisibleColumns, this.manageableColumnKeys);

      const updatedProfile: UserProfileDto = { ...this.userProfile, [this.columnSettingsTableType]: newVisibleColumns };
      this.userApiService.setUserProfile(updatedProfile).subscribe();
      this.userProfileChanged.emit(updatedProfile);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userProfile) {
      this.loadColumnSettings();
    }
  }

  protected loadColumnSettings(): void {
    const catalog = this.getColumnCatalog();
    this.manageableColumnKeys = catalog.map(c => c.key);
    const initialVisibleColumns = this.displayedColumns.filter(key => this.manageableColumnKeys.includes(key));

    const saved = this.userProfile?.[this.columnSettingsTableType]?.filter(key => this.manageableColumnKeys.includes(key));
    this.visibleManageableColumns = saved !== undefined ? saved : initialVisibleColumns;
    this.applyColumnSettings(this.visibleManageableColumns, this.manageableColumnKeys);
  }

  getYesNoOptions(): { name: string; value: any }[] {
    return [
      { name: this.translateService.translate('common.dictionary.yes'), value: 'true' },
      { name: this.translateService.translate('common.dictionary.no'), value: 'false' },
    ];
  }

  getManufacturingCountryOptions(): { name: string; value: any }[] {
    return this.coreDataUpdateService.allManufacturingCountries.map(c => ({ name: c.name, value: c.futureId }));
  }

  getDistributionCountryOptions(): { name: string; value: any }[] {
    return this.coreDataUpdateService.allDistributionCountries.map(c => ({ name: c.name, value: c.futureId }));
  }

  getPackagingLevelOptions(): { name: string; value: any }[] {
    return this.coreDataUpdateService.allPackagingLevels.map(l => ({ name: l.name, value: l.id }));
  }

  getPackagingLevelLabel(packagingLevelId: string | null): string {
    if (packagingLevelId == null) {
      return '';
    }
    return this.coreDataUpdateService.allPackagingLevelsMap.get(packagingLevelId)?.name ?? '';
  }

  ngOnDestroy(): void {
    this.moveBackendSubscription?.unsubscribe();
    this.copyBackendSubscription?.unsubscribe();
    this.organizationsSubscription?.unsubscribe();
    this.itemsInDirSubscription?.unsubscribe();
    this.deleteBackendSubscription?.unsubscribe();
    this.restoreSubscription?.unsubscribe();
    this.comparisonSubscription?.unsubscribe();
    this.dialogSubscription?.unsubscribe();
    this.permissionsSubscription?.unsubscribe();
    this.dataSourceSubscription?.unsubscribe();
    this.recSubscription?.unsubscribe();
    this.jsonSubscription?.unsubscribe();
    this.excelSubscription?.unsubscribe();
    this.exportBackendSubscription?.unsubscribe();
    this.columnOrganizerDialogSubscription?.unsubscribe();
  }
}
